import { useEffect, type CSSProperties, type ReactNode } from "react";
import { vars } from "@pack-it-up/ui-web";
import type { IncomingInvite } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useSession } from "../../app/SessionProvider.js";

const c = vars.color;
const sp = vars.space;
const r = vars.radius;

// Small bordered action buttons, matching the FriendsPage request rows.
const btnBase: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: `${sp.xs} ${sp.sm}`,
  borderRadius: r.xs,
  cursor: "pointer",
  fontFamily: vars.font.body,
  whiteSpace: "nowrap",
};

const roleLabel: Record<IncomingInvite["role"], string> = {
  owner: "owner",
  editor: "can edit",
  viewer: "view only",
};

// The invite inbox (T25): every invite addressed to the signed-in user, accepted
// or declined explicitly — sharing never silently joins anyone. Callable-backed,
// so the list refreshes on mount (store creation) and via the Refresh button.
export const InvitesPage = (): ReactNode => {
  const { inviteInbox } = useSession();
  const invites = useStoreValue(inviteInbox, (s) => s.invites);
  const loading = useStoreValue(inviteInbox, (s) => s.loading);
  const error = useStoreValue(inviteInbox, (s) => s.error);

  // No live stream behind this list (callable-backed), so opening the inbox
  // refetches — the sidebar badge only reflects the sign-in-time fetch otherwise.
  useEffect(() => void inviteInbox.getState().refresh(), [inviteInbox]);

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: `${sp.xl} 28px 60px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
          <h1 style={{ fontFamily: vars.font.heading, fontSize: vars.font.sizeXl, fontWeight: vars.font.weightBold, margin: 0, flex: 1 }}>
            Invites
          </h1>
          <button
            type="button"
            onClick={() => void inviteInbox.getState().refresh()}
            style={{ ...btnBase, background: "transparent", color: c.textMuted, border: `1px solid ${c.border}` }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {error !== null && (
          <div role="alert" style={{ fontSize: 12, color: c.danger, marginTop: sp.sm }}>
            {error}
          </div>
        )}

        <div
          style={{
            background: c.surface,
            border: `1px solid ${c.border}`,
            borderRadius: r.lg,
            overflow: "hidden",
            marginTop: 14,
          }}
        >
          {invites.length === 0 ? (
            <p style={{ padding: `${sp.xl} ${sp.lg}`, textAlign: "center", fontSize: 12, color: c.textMuted, margin: 0 }}>
              No pending invites.
            </p>
          ) : (
            invites.map((inv, i) => (
              <div
                key={inv.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: sp.md,
                  padding: `${sp.md} ${sp.lg}`,
                  borderTop: i === 0 ? "none" : `1px solid ${c.border}`,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {inv.resourceName !== "" ? inv.resourceName : `A ${inv.resource.kind}`}
                  </div>
                  <div style={{ fontSize: 11, color: c.textMuted, marginTop: 1 }}>
                    {inv.inviterName !== "" ? inv.inviterName : "Someone"} invited you · {roleLabel[inv.role]}
                  </div>
                </div>
                <div style={{ display: "flex", gap: sp.xs }}>
                  <button
                    type="button"
                    onClick={() => void inviteInbox.getState().accept(inv.id)}
                    style={{ ...btnBase, background: c.success, color: "#fff", border: "none" }}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={() => void inviteInbox.getState().decline(inv.id)}
                    style={{ ...btnBase, background: "transparent", color: c.textMuted, border: `1px solid ${c.border}` }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
